import React, { useMemo, useState } from "react";
import { Pressable, View } from "react-native";
import Animated, { useAnimatedStyle, withSpring, interpolateColor, useDerivedValue } from "react-native-reanimated";
import { StyleSheet, useUnistyles } from "react-native-unistyles";

const SwitchInternalPadding = 4;
const Padding = 3;

const TrackSizes = {
  small: {
    width: 32,
    height: 16,
  },
  medium: {
    width: 40,
    height: 20,
  },
  large: {
    width: 56,
    height: 28,
  },
};

const ThumbSizes = {
  small: 16 - SwitchInternalPadding,
  medium: 20 - SwitchInternalPadding,
  large: 26.4 - SwitchInternalPadding,
};

type SwitchProps = {
  active?: boolean;
  onToggle?: () => void;
  size?: "small" | "medium" | "large";
  disabled?: boolean;
};

export const Switch: React.FC<SwitchProps> = React.memo(
  ({ active = true, onToggle, size = "medium", disabled = false }) => {
    const { theme } = useUnistyles();
    const [isFocused, setIsFocused] = useState(false);

    const dynamicTrackStyles = useMemo(() => {
      return {
        width: TrackSizes[size].width,
        height: TrackSizes[size].height,
        borderRadius: TrackSizes[size].height / 2,
      };
    }, [size]);

    const dynamicThumbStyles = useMemo(() => {
      return {
        width: ThumbSizes[size],
        height: ThumbSizes[size],
        borderRadius: ThumbSizes[size] / 2,
      };
    }, [size]);

    const dynamicFocusedStyles = useMemo(() => {
      return {
        width: TrackSizes[size].width + theme.spacing.md,
        height: TrackSizes[size].height + theme.spacing.md,
        borderRadius: TrackSizes[size].height / 2 + theme.spacing.md,
      };
    }, [size, theme.spacing.md]);

    const offset = useDerivedValue(() => {
      return withSpring(active ? TrackSizes[size].width - ThumbSizes[size] - Padding : Padding, {
        damping: 55,
        stiffness: 400,
      });
    });

    const animatedStyles = useAnimatedStyle(() => {
      return {
        transform: [{ translateX: offset.value }],
      };
    });

    const trackAnimatedStyles = useAnimatedStyle(() => {
      if (disabled && active) {
        return { backgroundColor: theme.colors.interactive.controls.disabled_active_background, opacity: 0.4 };
      }
      if (disabled && !active) {
        return { backgroundColor: theme.colors.interactive.controls.disabled_background };
      }
      const backgroundColor = interpolateColor(
        offset.value,
        [0, TrackSizes[size].width - ThumbSizes[size]],
        [theme.colors.interactive.controls.default_midground, theme.colors.interactive.controls.selected_midground],
      );
      return { backgroundColor };
    }, [
      theme.colors.interactive.controls.disabled_active_background,
      theme.colors.interactive.controls.disabled_background,
      theme.colors.interactive.controls.default_midground,
      theme.colors.interactive.controls.selected_midground,
    ]);

    return (
      <View style={[isFocused && styles.focused, isFocused && dynamicFocusedStyles]}>
        <Pressable
          onPress={onToggle}
          disabled={disabled}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        >
          <Animated.View style={[styles.track, dynamicTrackStyles, trackAnimatedStyles]}>
            <Animated.View style={[styles.thumb, dynamicThumbStyles, animatedStyles]} />
          </Animated.View>
        </Pressable>
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  track: {
    justifyContent: "center",
  },
  thumb: {
    backgroundColor: theme.colors.white.default,
  },
  focused: {
    borderColor: theme.colors.interactive.controls.focused_border,
    borderWidth: 2,
    justifyContent: "center",
    alignItems: "center",
  },
}));
